import React, { useEffect, useRef, useState } from 'react';
import { FaReply, FaX } from 'react-icons/fa6';
import { LuChevronLeft, LuSendHorizonal } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { UserProfileInterface } from '../../../app-schemas/user.interface';
import { LuMoreVertical } from 'react-icons/lu';
import { TEDropdown, TEDropdownToggle, TEDropdownMenu, TEDropdownItem, TERipple } from 'tw-elements-react';
import apiClient from '../../../api-services/ApiClient';
import { formatTimestamp, getUser, truncateText } from '../../../app-utils';
import { useConversation } from '../../../api-services/messaging/useConversation';
import { useSendMessage } from '../../../api-services/messaging/useSendMessage';
import { SITELINK } from '../../../app-routes/Links';
import { Avatar } from '../../../assets/custom-icons';
import { useTheme } from '../../../context/theme-context';

export interface selectedUserInterface {
  id: number;
  name: string;
  image: string;
}

// Skeleton Component for Chat Messages
const ChatSkeleton: React.FC = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-hc_F4EBFA'}`}>
      {/* Header Skeleton */}
      <div className="bg-custom-600 dark:bg-slate-800 p-2 px-2 flex items-center justify-between fixed top-[-1px] right-0 left-0 z-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full animate-pulse" />
          <div className="w-10 h-10 rounded-full bg-white/20 animate-pulse" />
          <div className="w-32 h-6 bg-white/20 rounded animate-pulse" />
        </div>
        <div className="w-8 h-8 bg-white/20 rounded-full animate-pulse" />
      </div>

      {/* Messages Skeleton */}
      <div className="pt-16 pb-20 px-4">
        <div className="space-y-6">
          {/* Date header skeleton */}
          <div className="flex justify-center">
            <div className="w-32 h-6 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
          </div>
          
          {/* Message bubbles skeleton */}
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
              <div className={`rounded-2xl px-4 py-3 max-w-[75%] min-w-[120px] ${
                i % 2 === 0 
                  ? 'bg-blue-700/50 dark:bg-slate-700/50' 
                  : 'bg-gray-300/50 dark:bg-gray-700/50'
              } animate-pulse`}>
                <div className="h-4 bg-white/20 rounded w-32 mb-2" />
                <div className="h-3 bg-white/20 rounded w-16 ml-auto" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Input Skeleton */}
      <div className={`p-2 flex items-center fixed bottom-0 left-0 right-0 ${isDarkMode ? 'bg-slate-700' : 'bg-hc_F4EBFA'}`}>
        <div className="flex-1 h-[45px] bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
        <div className="ml-1 w-[45px] h-[45px] bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse" />
      </div>
    </div>
  );
};

// Error Component
const ChatError: React.FC<{ onRetry: () => void; error: any }> = ({ onRetry, error }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col items-center justify-center min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-hc_F4EBFA'}`}>
      <div className="text-center p-6">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
          isDarkMode ? 'bg-red-900/30' : 'bg-red-100'
        }`}>
          <svg className={`w-10 h-10 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Failed to load messages
        </h3>
        <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {error?.message || 'Unable to connect to the chat server. Please check your connection.'}
        </p>
        <button
          onClick={onRetry}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

// Empty State Component
const ChatEmpty: React.FC = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col items-center justify-center h-[80vh] ${isDarkMode ? 'bg-neutral-800' : 'bg-hc_F4EBFA'}`}>
      <div className="text-center">
        <svg className={`w-20 h-20 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No messages yet
        </p>
        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Send a message to start the conversation
        </p>
      </div>
    </div>
  );
};

const Chat: React.FC<{ selectedUser: selectedUserInterface; backToInbox: () => void }> = ({ selectedUser, backToInbox }) => {
  // navigate
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  // collect page resources
  const user: UserProfileInterface = getUser();

  const { data: messages, error, isLoading, refetch } = useConversation(selectedUser.id);
  const { mutate: sendMessageMutation } = useSendMessage();

  // selectedUser
  const selectedUserStr = JSON.stringify(selectedUser);
  localStorage.setItem('lastChattedUserInfo', selectedUserStr);

  // page states
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [replyToMessage, setReplyToMessage] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [touchStartX, setTouchStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [activeMessageId, setActiveMessageId] = useState<number | null>(null);
  const [showReplyIndicator, setShowReplyIndicator] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);

    console.log(replyTo)

  // page refs
  const inputRef = useRef<HTMLInputElement | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleReply = (messageId: number, messageText: string, senderName: string) => {
    setReplyTo(messageId);
    setReplyToMessage(`Replying to ${senderName}: ${truncateText(messageText, 30)}`);
    focusInput();
  };

  const focusInput = () => {
    const input = document.getElementById('myInput');
    if (input) {
      input.focus();
    }
  };

  const scrollDown = () => {
    const chatRefSection = document.getElementById('chatRef');
    if (chatRefSection) {
      chatRefSection.scrollTo({
        top: chatRefSection.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const sendMessage = (e: any) => {
    e.preventDefault();
    if (input.trim() === '') return;

    setSendingMessage(true);
    
    // Send message without reply_to if the API doesn't support it
    // The reply functionality is just for UI feedback
    sendMessageMutation(
      { receiver_id: selectedUser.id, message: input },
      {
        onSuccess: () => {
          setInput('');
          setReplyTo(null);
          setReplyToMessage(null);
          scrollDown();
          setSendingMessage(false);
        },
        onError: () => {
          setSendingMessage(false);
        }
      }
    );
  };

  const endChat = async (e: any) => {
    e.preventDefault();
    const isConfirm = window.confirm('Are you sure you want to end this chat?');
    if (!isConfirm) return;

    try {
      setLoading(true);
      await apiClient.patch(`chat/close/${selectedUser.id}`);
      navigate(SITELINK.CHAT);
      backToInbox();
    } catch (e) {
      console.error('Error ending chat:', e);
    } finally {
      setLoading(false);
    }
  };

  // Handle swipe start
  const handleTouchStart = (e: React.TouchEvent, messageId: number) => {
    setTouchStartX(e.touches[0].clientX);
    setActiveMessageId(messageId);
    setShowReplyIndicator(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!activeMessageId) return;

    const touchX = e.touches[0].clientX;
    const deltaX = touchX - touchStartX;

    if (deltaX > 0) {
      setDragOffset(deltaX);
      if (deltaX > 30) setShowReplyIndicator(true);
    }
  };

  const handleTouchEnd = () => {
    if (dragOffset > 50 && activeMessageId) {
      const message = messages?.find((msg: any) => msg.id === activeMessageId);
      if (message) {
        const senderName = message.sender_id === user.id ? 'You' : selectedUser.name;
        handleReply(activeMessageId, message.message, senderName);
      }
    }

    setDragOffset(0);
    setActiveMessageId(null);
    setShowReplyIndicator(false);
  };

  // Show skeleton while loading
  if (isLoading) {
    return <ChatSkeleton />;
  }

  // Show error state
  if (error) {
    return <ChatError onRetry={() => refetch()} error={error} />;
  }

  return (
    <div className={`flex flex-col min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-hc_F4EBFA'}`}>
      {/* Chat Header */}
      <div className="bg-custom-600 text-white dark:bg-slate-800 p-2 px-2 flex items-center justify-between fixed top-[-1px] right-0 left-0 z-50 shadow-md">
        <div className="flex items-center">
          <button
            onClick={() => {
              navigate(SITELINK.CHAT);
              localStorage.removeItem('lastChattedUserInfo');
              backToInbox();
            }}
            className="mr-4 text-white hover:bg-white/20 p-1 rounded-full transition-colors"
            aria-label="Go back"
          >
            <LuChevronLeft className="text-2xl" />
          </button>

          <div className="relative">
            <img
              src={selectedUser.image || Avatar}
              className="h-[40px] w-[40px] rounded-full shadow border-2 border-white dark:border-gray-600 object-cover"
              alt={selectedUser.name}
              onError={(e) => {
                (e.target as HTMLImageElement).src = Avatar;
              }}
            />
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></span>
          </div>

          <h1 className="text-lg font-medium ml-3 dark:text-white truncate max-w-[150px]">
            {selectedUser.name}
          </h1>
        </div>

        <div className="text-end pr-3">
          <TEDropdown className="flex justify-center">
            <TERipple rippleColor="light">
              <TEDropdownToggle className="p-2 rounded-full hover:bg-white/20 transition-colors">
                <LuMoreVertical className="text-xl" />
              </TEDropdownToggle>
            </TERipple>

            <TEDropdownMenu className={`${isDarkMode ? 'dark' : ''}`}>
              <TEDropdownItem>
                <button
                  onClick={endChat}
                  disabled={loading}
                  type="button"
                  className="block w-full min-w-[90px] cursor-pointer whitespace-nowrap bg-transparent px-4 py-2 text-sm text-left font-normal pointer-events-auto text-neutral-700 hover:bg-neutral-100 active:text-neutral-800 active:bg-neutral-100 focus:bg-neutral-100 focus:text-neutral-800 focus:outline-none active:no-underline dark:text-neutral-200 dark:hover:bg-neutral-600 dark:focus:bg-neutral-600 dark:active:bg-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Ending...' : 'End Chat'}
                </button>
              </TEDropdownItem>
            </TEDropdownMenu>
          </TEDropdown>
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={chatRef}
        id="chatRef"
        className="flex-1 pt-16 pb-20 overflow-y-auto"
        style={{
          height: '100vh',
          overflowY: 'auto',
          padding: '10px',
        }}
      >
        {!messages || messages.length === 0 ? (
          <ChatEmpty />
        ) : (
          <div className="pb-4">
            {messages?.map((message: any, index: number) => {
              const isPreviousMessageBySameUser =
                index > 0 && messages[index - 1]?.sender_id === message?.sender_id;

              const currentDate = formatTimestamp(message?.created_at, 'date');
              const previousMessageDate =
                index > 0 ? formatTimestamp(messages[index - 1]?.created_at, 'date') : null;

              const dateToDisplay =
                index === 0 || previousMessageDate !== currentDate ? currentDate : null;

              return (
                <div key={message.id || index}>
                  {dateToDisplay && (
                    <p className="w-full text-center text-xs pt-4 pb-4">
                      <span className={`p-2 px-4 rounded-xl ${isDarkMode ? 'bg-slate-800 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                        {dateToDisplay}
                      </span>
                    </p>
                  )}

                  <div
                    className={`flex ${
                      message.sender_id === user.id ? 'justify-end' : 'justify-start'
                    } ${isPreviousMessageBySameUser ? 'pt-0.5' : 'pt-4'}`}
                  >
                    <div
                      className={`rounded-2xl px-3 py-2 max-w-[75%] min-w-[100px] break-words transition-all ${
                        message.sender_id === user.id
                          ? `bg-blue-600 text-white rounded-br-none ${isDarkMode ? 'dark:bg-slate-700' : ''}`
                          : `${isDarkMode ? 'bg-gray-700 text-gray-100' : 'bg-gray-200 text-gray-900'} rounded-tl-none cursor-pointer hover:opacity-90`
                      }`}
                      onClick={() => {
                        const senderName = message.sender_id === user.id ? 'You' : selectedUser.name;
                        handleReply(message.id, message.message, senderName);
                      }}
                      onTouchStart={(e) => handleTouchStart(e, message.id)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      style={{
                        transform: `translateX(${activeMessageId === message.id ? dragOffset : 0}px)`,
                        transition: dragOffset === 0 ? 'transform 0.2s ease' : '',
                      }}
                    >
                      {showReplyIndicator && activeMessageId === message.id && (
                        <div className="absolute left-[-30px] top-1/2 transform -translate-y-1/2 text-blue-600 dark:text-blue-400 py-1 rounded shadow text-sm">
                          <FaReply />
                        </div>
                      )}

                      {/* Note: reply display is kept for UI but actual reply_to is not sent to API */}
                      {message.reply_to && (
                        <div
                          className={`border-l-4 pl-2 py-1 text-xs rounded-[5px] flex-col flex mb-1 ${
                            message.sender_id === user.id
                              ? `border-blue-400 ${isDarkMode ? 'bg-slate-800' : 'bg-blue-500'} text-gray-100`
                              : `${isDarkMode ? 'border-gray-500 bg-gray-800' : 'border-blue-400 bg-gray-300'}`
                          }`}
                        >
                          <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {messages.find((msg: any) => msg.id === message.reply_to)?.sender_id === user.id
                              ? 'You'
                              : selectedUser.name}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {truncateText(
                              messages.find((msg: any) => msg.id === message.reply_to)?.message,
                              50,
                            )}
                          </span>
                        </div>
                      )}

                      <p className="px-1 text-sm leading-relaxed">{message.message}</p>
                      <p className={`text-end p-1 text-[10px] ${message.sender_id === user.id ? 'text-blue-100' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {formatTimestamp(message.created_at, 'time')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Sending indicator */}
            {sendingMessage && (
              <div className="flex justify-end pt-2">
                <div className={`rounded-2xl px-4 py-2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className={`p-2 flex items-center fixed bottom-0 left-0 right-0 shadow-lg ${
        isDarkMode ? 'bg-slate-700 border-t border-gray-600' : 'bg-hc_F4EBFA border-t border-gray-200'
      }`}>
        <div className="relative flex-1">
          <input
            autoComplete="off"
            ref={inputRef}
            id="myInput"
            type="text"
            className={`w-full px-4 py-2 rounded-full shadow-sm focus:outline-none focus:ring-2 transition-all h-[45px] ${
              isDarkMode 
                ? `bg-slate-800 text-gray-100 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 ${replyToMessage ? 'pr-10' : ''}`
                : `bg-gray-100 text-gray-900 placeholder-gray-500 focus:ring-blue-400 focus:border-blue-400 ${replyToMessage ? 'pr-10' : ''}`
            }`}
            placeholder={replyToMessage || 'Type a message...'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(e)}
            disabled={sendingMessage}
          />
          {replyToMessage && (
            <button
              type="button"
              className="absolute top-3 right-3 hover:scale-110 transition-transform"
              onMouseDown={() => {
                setReplyTo(null);
                setReplyToMessage(null);
                inputRef.current?.focus();
              }}
              tabIndex={-1}
              aria-label="Cancel reply"
            >
              <FaX className="text-red-500 bg-white dark:bg-gray-600 p-1 rounded-full text-lg" />
            </button>
          )}
        </div>

        <button
          type="button"
          className={`ml-2 flex items-center justify-center rounded-full h-[45px] w-[45px] shadow transition-all ${
            input.trim() && !sendingMessage
              ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
              : 'bg-gray-400 cursor-not-allowed opacity-50'
          }`}
          onMouseDown={(e) => sendMessage(e)}
          disabled={!input.trim() || sendingMessage}
          tabIndex={-1}
          aria-label="Send message"
        >
          <LuSendHorizonal className="text-2xl text-white" />
        </button>
      </div>
    </div>
  );
};

export default Chat;